import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { api, ApiError } from '../api/client';

interface AuthState {
  user: any | null;
  loading: boolean;
  login: (username: string, password: string) => Promise<void>;
  applyLogin: (res: any, email: string) => void;
  logout: () => void;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthState>({
  user: null,
  loading: true,
  login: async () => {},
  applyLogin: () => {},
  logout: () => {},
  isAuthenticated: false,
});

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (api.isAuthenticated()) {
      // Token exists - restore session from stored user info
      const stored = localStorage.getItem('admin_user');
      if (stored) {
        try { setUser(JSON.parse(stored)); } catch { api.clearToken(); }
      } else {
        // Token but no stored user - clear stale token
        api.clearToken();
      }
    }
    setLoading(false);
  }, []);

  const login = useCallback(async (username: string, password: string) => {
    localStorage.removeItem('platform_tenant_id');   // өөр эрхээр сонгосон хуучин СӨХ үлдэхгүй (захирлаар нэвтрэхэд 403 → хоосон хуудас гардаг байсан)
    const res = await api.post('/auth/admin/login', { email: username, password });
    api.setToken(res.access_token);
    if (res.refresh_token) api.setRefreshToken(res.refresh_token);
    // Decode JWT payload to get role
    let role = 'manager';
    try {
      const payload = JSON.parse(atob(res.access_token.split('.')[1]));
      role = payload.role || 'manager';
    } catch {}
    const u = res.user || { username, role };
    if (!u.role) u.role = role;
    localStorage.setItem('admin_user', JSON.stringify(u));
    setUser(u);
  }, []);

  /** OTP/бүртгэлийн хариунаас (access_token, refresh_token, user?) нэвтрэлт тавина. */
  const applyLogin = useCallback((res: any, email: string) => {
    api.setToken(res.access_token);
    if (res.refresh_token) api.setRefreshToken(res.refresh_token);
    let role = 'manager', tenant_id: string | undefined;
    try { const p = JSON.parse(atob(res.access_token.split('.')[1])); role = p.role || role; tenant_id = p.tenant_id; } catch {}
    const u = res.user || { email, role, tenant_id: res.tenant_id || tenant_id };
    if (!u.role) u.role = role;
    localStorage.setItem('admin_user', JSON.stringify(u));
    setUser(u);
  }, []);

  const logout = useCallback(() => {
    api.clearToken();
    localStorage.removeItem('admin_user');
    setUser(null);
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, login, applyLogin, logout, isAuthenticated: !!user }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
